

// services/TaskService.js
import axiosInstance from "../utils/axiosInstance.js";
import API_BASE_URL from "./common/common.js";

// Cache for storing API responses to avoid repeated calls
const cache = {
  data: new Map(),
  timestamps: new Map(),
  // Cache duration in milliseconds (5 minutes default, configurable via env)
  CACHE_DURATION: parseInt(process.env.REACT_APP_CACHE_DURATION) || 5 * 60 * 1000,
  
  set(key, value) {
    this.data.set(key, value);
    this.timestamps.set(key, Date.now());
  },
  
  get(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return null;
    
    // Check if cache has expired
    if (Date.now() - timestamp > this.CACHE_DURATION) {
      this.data.delete(key);
      this.timestamps.delete(key);
      return null;
    }
    
    return this.data.get(key);
  },
  
  clear() {
    this.data.clear();
    this.timestamps.clear();
  },
  
  clearExpired() {
    const now = Date.now();
    for (const [key, timestamp] of this.timestamps.entries()) {
      if (now - timestamp > this.CACHE_DURATION) {
        this.data.delete(key);
        this.timestamps.delete(key);
      }
    }
  }
};

// Track in-flight API calls to prevent duplicate concurrent requests
const inFlightRequests = new Map();

// Per cacheKey generation: when a newer fetch starts, older completions must not overwrite cache
const latestFetchGeneration = new Map();

class TaskService {
  /**
   * Ensure token is valid before making API calls
   * @private
   */
  static async ensureValidToken() {
    // Token validation is now handled automatically by axiosInstance interceptor
    // This method is kept for backward compatibility but no longer needed
    // The interceptor will validate and refresh tokens before every request
    return Promise.resolve();
  }

  /**
   * Generic cached API call method
   * @private
   */
  static async cachedApiCall(cacheKey, apiCall, options = {}) {
    const { bypassCache = false, cacheFor = cache.CACHE_DURATION } = options;
    
    // Check cache first (unless bypassed)
    if (!bypassCache) {
      const cachedResult = cache.get(cacheKey);
      if (cachedResult !== null) {
        console.log(`📦 Cache hit for: ${cacheKey}`);
        return cachedResult;
      }
    }
    
    // Coalesce concurrent requests for the same key (including bypassCache) so two callers
    // in the same tick do not duplicate HTTP calls. Sequential bypass calls still start fresh
    // after the previous inFlight entry is cleared in finally.
    if (inFlightRequests.has(cacheKey)) {
      console.log(`⏳ Joining in-flight request for: ${cacheKey}`);
      return inFlightRequests.get(cacheKey);
    }

    console.log(`🌐 Making API call for: ${cacheKey}${bypassCache ? " (bypass cache)" : ""}`);

    const requestPromise = (async () => {
      const myGeneration = (latestFetchGeneration.get(cacheKey) || 0) + 1;
      latestFetchGeneration.set(cacheKey, myGeneration);

      try {
        await this.ensureValidToken();
        const result = await apiCall();

        // Only the newest fetch for this key may populate cache (avoids stale overwrite)
        if (latestFetchGeneration.get(cacheKey) === myGeneration) {
          cache.set(cacheKey, result);
          console.log(`✅ Cached result for: ${cacheKey}`);
        } else {
          console.log(`⏭️ Skipped cache write for stale response: ${cacheKey}`);
        }

        return result;
      } catch (error) {
        console.error(`❌ API call failed for ${cacheKey}:`, error);
        throw error;
      } finally {
        if (inFlightRequests.get(cacheKey) === requestPromise) {
          inFlightRequests.delete(cacheKey);
        }
      }
    })();

    inFlightRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }

  /**
   * Batch API calls for better performance
   * @private
   */
  static async batchApiCalls(calls) {
    const results = await Promise.allSettled(calls.map(call => call()));
    
    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        console.error(`Batch API call ${index} failed:`, result.reason);
        throw result.reason;
      }
    });
  }

  /**
   * Fetch user role details by email with caching
   * @param {string} email - User's email address
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to user role details
   */
  static async fetchUserRole(email, forceRefresh = false) {
    const cacheKey = `user_role_${email}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      console.log("Making API call to fetchUserRole with email:", email);
      
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchUserRole?user_email=${email}`
      );
      
      console.log("fetchUserRole API response:", response);
      
      if (!response.data) {
        console.error(`❌ User role for email ${email} not found - empty response`);
        throw new Error(`User role for email ${email} not found`);
      }
      
      // Response structure: { user_roles: [...] } (lookup lists come from fetchLookupMasterData)
      const responseData = response.data;
      
      // Validate that we have the expected structure
      if (!responseData.user_roles || responseData.user_roles.length === 0) {
        console.error(`❌ User roles array is empty for email ${email}`);
        throw new Error(`User role for email ${email} not found`);
      }
      
      console.log("Extracted response data:", responseData);
      
      return responseData;
    }, { bypassCache: forceRefresh });
  }

  /**
   * Internal user dashboard (role 4 / 5): nested counts by application / priority / type / status bucket.
   * @param {string} userEmail
   * @param {boolean} forceRefresh
   * @param {"my_tasks"|"my_team"} viewType
   * @returns {Promise<Object>} API body: either `{ InternalTaskTracker: { [appName]: { Critical|High|Medium|Low → … } } }` or a flat `{ [appName]: { Critical|High|Medium|Low → … } }` map (see parseInternalTaskTrackerPayload).
   */
  static async fetchInternalUserDashboard(userEmail, forceRefresh = false, viewType = "my_tasks") {
    const resolvedViewType = viewType === "my_team" ? "my_team" : "my_tasks";
    const cacheKey = `internal_user_dashboard_${userEmail}_${resolvedViewType}`;

    return this.cachedApiCall(
      cacheKey,
      async () => {
        const url =
          `${API_BASE_URL}/fetchInternalUserDashboard` +
          `?user_email=${encodeURIComponent(userEmail)}` +
          `&view_type=${encodeURIComponent(resolvedViewType)}`;
        console.log("📊 fetchInternalUserDashboard:", url);
        const response = await axiosInstance.get(url);
        if (!response.data) {
          throw new Error("fetchInternalUserDashboard returned empty response");
        }
        return response.data;
      },
      { bypassCache: forceRefresh }
    );
  }

  /**
   * Master lookup data (applications, statuses, release types, task types) — no user email in payload.
   * @param {boolean} forceRefresh - Bypass cache
   * @returns {Promise<Object>} application_details, task_status_details, release_type_details, task_type_details
   */
  static async fetchLookupMasterData(forceRefresh = false) {
    const cacheKey = "lookup_master_data";

    return this.cachedApiCall(
      cacheKey,
      async () => {
        console.log("Making API call to fetchLookupMasterData");
        const response = await axiosInstance.get(
          `${API_BASE_URL}/fetchLookupMasterData`
        );
        if (!response.data) {
          throw new Error("fetchLookupMasterData returned empty response");
        }
        console.log("fetchLookupMasterData API response keys:", Object.keys(response.data));
        return response.data;
      },
      { bypassCache: forceRefresh }
    );
  }

  /**
   * Fetch task priority options from the API with caching
   * @returns {Promise} Promise that resolves to an array of priority objects
   */
  static async fetchTaskPriorityDetails() {
    const cacheKey = 'task_priorities';
    
    return this.cachedApiCall(cacheKey, async () => {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchTaskPriorityDetails`
      );
      return response.data;
    });
  }

  /**
   * Fetch task type options from the API with caching
   * @returns {Promise} Promise that resolves to an array of task type objects
   */
  static async fetchTaskTypeDetails() {
    const cacheKey = 'task_types';
    
    return this.cachedApiCall(cacheKey, async () => {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchTaskTypeDetails`
      );
      return response.data;
    });
  }

  /**
   * Fetch task status options from the API with caching
   * @returns {Promise} Promise that resolves to an array of status objects
   */
  static async fetchTaskStatusDetails() {
    const cacheKey = 'task_statuses';
    
    return this.cachedApiCall(cacheKey, async () => {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchTaskStatusDetails`
      );
      return response.data;
    });
  }

  /**
   * Batch fetch all static data (priorities, types, statuses) in one call
   * @returns {Promise} Promise that resolves to an object with all static data
   */
  static async fetchAllStaticData() {
    const cacheKey = 'all_static_data';
    
    return this.cachedApiCall(cacheKey, async () => {
      console.log("🚀 Fetching all static data in batch...");
      
      const [priorities, types, statuses] = await Promise.all([
        axiosInstance.get(`${API_BASE_URL}/fetchTaskPriorityDetails`),
        axiosInstance.get(`${API_BASE_URL}/fetchTaskTypeDetails`),
        axiosInstance.get(`${API_BASE_URL}/fetchTaskStatusDetails`)
      ]);

      const result = {
        priorities: priorities.data,
        types: types.data,
        statuses: statuses.data
      };

      console.log("✅ All static data fetched successfully");
      return result;
    });
  }

  /**
   * Fetch all lookup details (task types, priorities, statuses, employees, applications) in one call
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to an object with all lookup data
   */
  static async fetchAllLookupDetails(forceRefresh = false) {
    const cacheKey = 'all_lookup_details';

    if (forceRefresh) {
      cache.data.delete(cacheKey);
      cache.timestamps.delete(cacheKey);
    }

    return this.cachedApiCall(cacheKey, async () => {
      console.log("🚀 Fetching all lookup details in batch...");

      const userEmail = localStorage.getItem("userId") || "";
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchAllLookupDetails?user_email=${encodeURIComponent(userEmail)}`
      );

      console.log("✅ All lookup details fetched successfully");
      return response.data;
    }, { bypassCache: forceRefresh });
  }

  /** Drop cached employee/lookup data so the next fetch hits the server (e.g. after admin changes roles). */
  static invalidateLookupCaches() {
    ["all_lookup_details", "lookup_master_data"].forEach((key) => {
      cache.data.delete(key);
      cache.timestamps.delete(key);
      inFlightRequests.delete(key);
    });
    console.log("🗑️ Lookup caches invalidated");
  }

  /**
   * Create a new task (invalidates relevant caches)
   * @param {Object|FormData} taskData - The task data to create (can be JSON object or FormData for file uploads)
   * @param {boolean} hasFiles - Whether the payload contains files (FormData)
   * @returns {Promise} Promise that resolves to the created task
   */
  static async createTask(taskData, hasFiles = false) {
    try {
      await this.ensureValidToken();
      
      const config = {
        headers: {}
      };
      
      // If sending files via FormData, let the browser set Content-Type automatically
      // Otherwise, set Content-Type to application/json
      if (!hasFiles) {
        config.headers['Content-Type'] = 'application/json';
      }
      // For FormData, we don't set Content-Type - browser will set it with boundary
      
      const response = await axiosInstance.post(
        `${API_BASE_URL}/createNewTask`,
        taskData,
        config
      );
      
      // Invalidate tasks cache since we created a new task
      this.invalidateTasksCaches();
      
      return response.data;
    } catch (error) {
      console.error("Error creating task:", error);
      throw error;
    }
  }

  /**
   * Update an existing task (invalidates relevant caches)
   * @param {string} taskId - The ID of the task to update
   * @param {Object} taskData - The updated task data
   * @returns {Promise} Promise that resolves to the updated task
   */
  static async updateTask(taskId, taskData) {
    try {
      await this.ensureValidToken();
      
      const config = {
        headers: {}
      };
      
      // Check if taskData is FormData (has files) or regular JSON
      const isFormData = taskData instanceof FormData;
      
      // If sending files via FormData, let the browser set Content-Type automatically
      // Otherwise, set Content-Type to application/json
      if (!isFormData) {
        config.headers['Content-Type'] = 'application/json';
      }
      // For FormData, we don't set Content-Type - browser will set it with boundary
      
      const response = await axiosInstance.post(
        `${API_BASE_URL}/createNewTask`,
        taskData,
        config
      );
      
      // Invalidate caches for tasks and specific task
      this.invalidateTasksCaches();
      cache.data.delete(`task_${taskId}`);
      
      return response.data;
    } catch (error) {
      console.error("Error updating task:", error);
      throw error;
    }
  }

  /**
   * Update the status of a task (invalidates relevant caches)
   * @param {string} taskId - The ID of the task
   * @param {Object} statusData - The updated status data
   * @returns {Promise} Promise that resolves to the updated task
   */
  static async updateTaskStatus(taskId, statusData) {
    try {
      await this.ensureValidToken();
      
      const config = {
        headers: {}
      };
      
      // Check if statusData is FormData (has files) or regular JSON
      const isFormData = statusData instanceof FormData;
      
      // If sending files via FormData, let the browser set Content-Type automatically
      // Otherwise, set Content-Type to application/json
      if (!isFormData) {
        config.headers['Content-Type'] = 'application/json';
      }
      // For FormData, we don't set Content-Type - browser will set it with boundary
      
      const response = await axiosInstance.post(
        `${API_BASE_URL}/createNewTask`,
        statusData,
        config
      );
      
      // Invalidate caches for tasks and specific task
      this.invalidateTasksCaches();
      cache.data.delete(`task_${taskId}`);
      
      return response.data;
    } catch (error) {
      console.error("Error updating task status:", error);
      throw error;
    }
  }

  /**
   * Fetch all tasks from the API with role-based filtering and caching
   * @param {Object} filters - Filter parameters object
   * @param {Array<string>} filters.statuses - Array of status names to filter by
   * @param {Array<string>} filters.priorities - Array of priority names to filter by
   * @param {Array<number>} filters.applicationIds - Array of application IDs to filter by
   * @param {Array<string>} filters.taskTypes - Array of task type short names to filter by
   * @param {Array<string>} [filters.agingBuckets] - Age bucket codes (LT_7, LT_30, … GT_180), same as Ticket Ageing Report
   * @param {boolean} [filters.overdueTasks] - When true, overdue-only is requested; omitted or false adds no `overdue_tasks` query param
   * @param {boolean} [filters.unassignedTasks] - When true, unassigned-only is requested as `unassigned=true`
   * @param {Array<number>} filters.releaseTypeIds - Array of release type IDs to filter by
   * @param {Array<string|number>} filters.taskOwnerIds - Task owner employee UIDs (advanced filter; sent as task_owner_ids)
   * @param {Array<string|number>} filters.createdByIds - Task creator employee UIDs (advanced; sent as created_by_ids)
   * @param {string} filters.searchTerm - Search term to filter tasks
   * @param {string} filters.ownershipFilter - Ownership filter ('All', 'Assigned', 'TaskOwner', or lead name)
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to an array of tasks
   */
  static async getAllTasks(filters = {}, forceRefresh = false) {
    // Get role_id and default_application_id from localStorage
    const userRoleId = localStorage.getItem("user_role_id");
    const defaultApplicationId = localStorage.getItem("default_application_id");
    const currentUserId = localStorage.getItem("employee_uid");
    
    // Create cache key based on user context and filters
    const filterKey = JSON.stringify(filters);
    const cacheKey = `all_tasks_${userRoleId}_${defaultApplicationId || 'no_app'}_${filterKey}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      console.log("📡 TaskService.getAllTasks - Role ID from localStorage:", userRoleId);
      console.log("📡 TaskService.getAllTasks - Default Application ID from localStorage:", defaultApplicationId);
      console.log("📡 TaskService.getAllTasks - Filters:", filters);
      
      // Build the URL with query parameters
      let url = `${API_BASE_URL}/fetchAllTaskDetails`;
      const queryParams = [];
      
      // Add role_id if it exists
      if (userRoleId) {
        queryParams.push(`role_id=${encodeURIComponent(userRoleId)}`);
        console.log("✅ Adding role_id query parameter:", userRoleId);
        
        // If role_id is 3, add application_id parameter
        if (userRoleId === "3" && defaultApplicationId) {
          queryParams.push(`application_id=${encodeURIComponent(defaultApplicationId)}`);
          console.log("✅ Adding application_id query parameter for role_id=3:", defaultApplicationId);
        }
      }
      
      // Add status filter - pass as comma-separated status names or IDs
      if (filters.statuses && Array.isArray(filters.statuses) && filters.statuses.length > 0) {
        const statusParam = filters.statuses.map(s => encodeURIComponent(s)).join(',');
        queryParams.push(`statuses=${statusParam}`);
        console.log("✅ Adding statuses query parameter:", filters.statuses);
      }
      
      // Add priority filter - pass as comma-separated priority names
      if (filters.priorities && Array.isArray(filters.priorities) && filters.priorities.length > 0) {
        const priorityParam = filters.priorities.map(p => encodeURIComponent(p)).join(',');
        queryParams.push(`priorities=${priorityParam}`);
        console.log("✅ Adding priorities query parameter:", filters.priorities);
      }
      
      // Add application filter - pass as comma-separated application IDs
      if (filters.applicationIds && Array.isArray(filters.applicationIds) && filters.applicationIds.length > 0) {
        const appParam = filters.applicationIds.map(id => encodeURIComponent(id)).join(',');
        queryParams.push(`application_ids=${appParam}`);
        console.log("✅ Adding application_ids query parameter:", filters.applicationIds);
      }

      // Add task type filter - pass as comma-separated task type short names
      if (filters.taskTypes && Array.isArray(filters.taskTypes) && filters.taskTypes.length > 0) {
        const taskTypeParam = filters.taskTypes.map(t => encodeURIComponent(t)).join(',');
        queryParams.push(`task_types=${taskTypeParam}`);
        console.log("✅ Adding task_types query parameter:", filters.taskTypes);
      }

      // Age bucket filter — same codes / param as Ticket Ageing Report
      if (filters.agingBuckets && Array.isArray(filters.agingBuckets) && filters.agingBuckets.length > 0) {
        const bucketParam = filters.agingBuckets
          .map((b) => encodeURIComponent(String(b).trim()))
          .filter((s) => s !== "")
          .join(",");
        if (bucketParam) {
          queryParams.push(`aging_buckets=${bucketParam}`);
          console.log("✅ Adding aging_buckets query parameter:", filters.agingBuckets);
        }
      }

      if (filters.overdueTasks === true) {
        queryParams.push("overdue_tasks=true");
        console.log("✅ Adding overdue_tasks=true query parameter");
      }

      if (filters.unassignedTasks === true) {
        queryParams.push("unassigned=true");
        console.log("✅ Adding unassigned=true query parameter");
      }

      // Add release type filter - pass as comma-separated release type IDs
      if (filters.releaseTypeIds && Array.isArray(filters.releaseTypeIds) && filters.releaseTypeIds.length > 0) {
        const releaseTypeParam = filters.releaseTypeIds.map((id) => encodeURIComponent(id)).join(',');
        queryParams.push(`release_type_ids=${releaseTypeParam}`);
        console.log("✅ Adding release_type_ids query parameter:", filters.releaseTypeIds);
      }

      // Advanced: one or more task owner employee UIDs (distinct from ownershipFilter owner_id)
      if (filters.taskOwnerIds && Array.isArray(filters.taskOwnerIds) && filters.taskOwnerIds.length > 0) {
        const taskOwnerParam = filters.taskOwnerIds
          .map((id) => encodeURIComponent(String(id).trim()))
          .filter((s) => s !== "")
          .join(",");
        if (taskOwnerParam) {
          queryParams.push(`task_owner_ids=${taskOwnerParam}`);
          console.log("✅ Adding task_owner_ids query parameter:", filters.taskOwnerIds);
        }
      }

      // Advanced: task creator employee UIDs (fetchAllTaskDetails may filter server-side when supported)
      if (filters.createdByIds && Array.isArray(filters.createdByIds) && filters.createdByIds.length > 0) {
        const createdByParam = filters.createdByIds
          .map((id) => encodeURIComponent(String(id).trim()))
          .filter((s) => s !== "")
          .join(",");
        if (createdByParam) {
          queryParams.push(`created_by_ids=${createdByParam}`);
          console.log("✅ Adding created_by_ids query parameter:", filters.createdByIds);
        }
      }
      
      // Add search term filter
      // Check if searchTerm exists and is not empty (after trimming)
      if (filters.searchTerm !== undefined && filters.searchTerm !== null) {
        const trimmedSearch = String(filters.searchTerm).trim();
        if (trimmedSearch !== '') {
          queryParams.push(`search=${encodeURIComponent(trimmedSearch)}`);
          console.log("✅ Adding search query parameter:", trimmedSearch);
          console.log("🔍 Search parameter details - Original:", filters.searchTerm, "Trimmed:", trimmedSearch, "Encoded:", encodeURIComponent(trimmedSearch));
        } else {
          console.log("⚠️ Search term is empty or whitespace, skipping search parameter");
        }
      } else {
        console.log("⚠️ Search term is undefined or null, skipping search parameter");
      }
      
      // Add ownership filter
      if (filters.ownershipFilter) {
        if (filters.ownershipFilter === 'Assigned' && currentUserId) {
          queryParams.push(`assigned_to=${encodeURIComponent(currentUserId)}`);
          console.log("✅ Adding assigned_to query parameter:", currentUserId);
        } else if (filters.ownershipFilter === 'TaskOwner' && currentUserId) {
          queryParams.push(`owner_id=${encodeURIComponent(currentUserId)}`);
          console.log("✅ Adding owner_id query parameter:", currentUserId);
        } else if (filters.ownershipFilter !== 'All') {
          // It's a lead name filter
          queryParams.push(`lead_name=${encodeURIComponent(filters.ownershipFilter)}`);
          console.log("✅ Adding lead_name query parameter:", filters.ownershipFilter);
        }
        // For 'All', we don't add any ownership filter parameter
      }
      
      // Add pagination parameters
      if (filters.offset !== undefined && filters.offset !== null) {
        queryParams.push(`offset=${encodeURIComponent(filters.offset)}`);
        console.log("✅ Adding offset query parameter:", filters.offset);
      }
      
      if (filters.limit !== undefined && filters.limit !== null) {
        queryParams.push(`limit=${encodeURIComponent(filters.limit)}`);
        console.log("✅ Adding limit query parameter:", filters.limit);
      }
      
      // Add sorting parameters (supports multiple column sorting with comma-separated values)
      if (filters.sortBy && filters.sortBy.trim() !== '') {
        queryParams.push(`sort_by=${encodeURIComponent(filters.sortBy.trim())}`);
        console.log("✅ Adding sort_by query parameter (supports multiple columns):", filters.sortBy);
      }
      
      if (filters.sortOrder && filters.sortOrder.trim() !== '') {
        // Validate that sortOrder contains only 'asc' or 'desc' values (comma-separated)
        const sortOrders = filters.sortOrder.split(',');
        const isValid = sortOrders.every(order => order.trim() === 'asc' || order.trim() === 'desc');
        if (isValid) {
          queryParams.push(`sort_order=${encodeURIComponent(filters.sortOrder.trim())}`);
          console.log("✅ Adding sort_order query parameter (supports multiple orders):", filters.sortOrder);
        } else {
          console.warn("⚠️ Invalid sort_order values, skipping:", filters.sortOrder);
        }
      }
      
      // Append query parameters to URL if any exist
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      
      console.log("🔗 Final API URL:", url);
      
      const response = await axiosInstance.get(url);
      
      console.log("📦 TaskService.getAllTasks - API Response received, data length:", response.data?.data?.length || response.data?.length);
      
      // Return full response if it has pagination metadata, otherwise return data array
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return response.data; // Return { data: [], limit, offset, total_count }
      }
      
      return response.data; // Return array for backward compatibility
    }, { bypassCache: forceRefresh });
  }

  /**
   * Fetch dashboard statistics from the API with caching
   * @param {number} assignedTo Assigned To ID
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to dashboard statistics object
   */
  static async getDashboardStats(assignedTo, forceRefresh = false) {
    // Get additional parameters from localStorage
    const userRoleId = localStorage.getItem("user_role_id");
    const employeeUid = localStorage.getItem("employee_uid");
    const defaultApplicationId = localStorage.getItem("default_application_id");
    
    // Create cache key based on all parameters
    const cacheKey = `dashboard_stats_${assignedTo}_${userRoleId}_${employeeUid}_${defaultApplicationId || 'no_app'}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      console.log("📊 Dashboard Stats - Parameters from localStorage:");
      console.log("- assignedTo:", assignedTo);
      console.log("- userRoleId:", userRoleId);
      console.log("- employeeUid (owner_id):", employeeUid);
      console.log("- defaultApplicationId:", defaultApplicationId);
      
      // Build the URL with query parameters
      let url = `${API_BASE_URL}/fetchCountOfManagerDashboard?aa_app_task_assigned_to=${assignedTo}`;
      
      // Add role_id if it exists
      if (userRoleId) {
        url += `&role_id=${userRoleId}`;
      }
      
      // Add owner_id if it exists
      if (employeeUid) {
        url += `&owner_id=${employeeUid}`;
      }
      
      // Add application_id if it exists
      if (defaultApplicationId) {
        url += `&application_id=${defaultApplicationId}`;
      }
      
      console.log("🔗 Final Dashboard Stats API URL:", url);
      
      const response = await axiosInstance.get(url);

      if (!response.data || response.data.length === 0) {
        throw new Error(`Dashboard stats for assigned ID ${assignedTo} not found`);
      }

      console.log("📦 Dashboard Stats API Response:", response.data[0]);
      return response.data[0];
    }, { bypassCache: forceRefresh });
  }

  /**
   * Get task details by ID with caching
   * @param {number} taskId Task ID
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to task details
   */
  static async getTaskById(taskId, forceRefresh = false) {
    const cacheKey = `task_${taskId}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchTaskDetailsById?aa_app_rl_resource_task_uid=${taskId}`
      );

      if (!response.data || response.data.length === 0) {
        throw new Error(`Task with ID ${taskId} not found`);
      }

      const taskDetails = response.data[0];
      
      console.log('📎 Task details received:', taskDetails);
      
      // Initialize attachments array from main response (if any)
      let mainResponseAttachments = [];
      
      // Check all possible locations for attachments in main response
      if (taskDetails.file_attachments && Array.isArray(taskDetails.file_attachments)) {
        console.log('📎 Attachments found in file_attachments (base64 format):', taskDetails.file_attachments);
        mainResponseAttachments = taskDetails.file_attachments;
      } 
      else if (taskDetails.attachments && Array.isArray(taskDetails.attachments)) {
        console.log('📎 Attachments found in task details:', taskDetails.attachments);
        mainResponseAttachments = taskDetails.attachments;
      } 
      else if (taskDetails.aa_app_task_attachments) {
        // Handle if attachments are in a different field
        mainResponseAttachments = Array.isArray(taskDetails.aa_app_task_attachments) 
          ? taskDetails.aa_app_task_attachments 
          : [];
        console.log('📎 Attachments found in aa_app_task_attachments:', mainResponseAttachments);
      }
      
      // Always fetch attachments from separate endpoint to ensure we get all attachments
      let separateEndpointAttachments = [];
      try {
        separateEndpointAttachments = await this.getTaskAttachments(taskId, forceRefresh);
        console.log('📎 Attachments fetched from separate endpoint:', separateEndpointAttachments);
      } catch (error) {
        console.warn(`Could not fetch attachments from separate endpoint for task ${taskId}:`, error);
        console.warn('Error details:', error.response?.data || error.message);
        // Continue with main response attachments if separate endpoint fails
      }
      
      // Merge attachments from both sources, avoiding duplicates
      // Create a map to track unique attachments by ID or saved name
      const attachmentMap = new Map();
      
      // First, add attachments from main response (may have base64 content)
      mainResponseAttachments.forEach(att => {
        const key = att.aa_app_task_file_attachment_id || 
                   att.aa_app_task_file_saved_name || 
                   att.id || 
                   att.saved_name || 
                   att.fileName || 
                   `${att.aa_app_task_file_original_name || att.name || att.file_name || ''}_${att.aa_app_task_file_upload_date || att.upload_date || ''}`;
        
        if (key && !attachmentMap.has(key)) {
          attachmentMap.set(key, att);
        }
      });
      
      // Then, add attachments from separate endpoint (may have different/more data)
      separateEndpointAttachments.forEach(att => {
        const key = att.aa_app_task_file_attachment_id || 
                   att.aa_app_task_file_saved_name || 
                   att.id || 
                   att.saved_name || 
                   att.fileName || 
                   `${att.aa_app_task_file_original_name || att.name || att.file_name || ''}_${att.aa_app_task_file_upload_date || att.upload_date || ''}`;
        
        if (key) {
          // If attachment already exists, merge properties (prefer base64 from main, other data from separate)
          if (attachmentMap.has(key)) {
            const existing = attachmentMap.get(key);
            // Merge: keep base64 from main if it exists, otherwise use from separate
            attachmentMap.set(key, {
              ...existing,
              ...att,
              // Prefer base64 content from main response if available
              aa_app_task_file_content_base64: existing.aa_app_task_file_content_base64 || att.aa_app_task_file_content_base64 || existing.base64_content || att.base64_content
            });
          } else {
            attachmentMap.set(key, att);
          }
        }
      });
      
      // Convert map back to array
      taskDetails.attachments = Array.from(attachmentMap.values());
      
      console.log(`📎 Total attachments after merging: ${taskDetails.attachments.length}`, taskDetails.attachments);

      return taskDetails;
    }, { bypassCache: forceRefresh });
  }

  /**
   * Fetch attachments for a task
   * @param {number} taskId Task ID
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to an array of attachment objects
   */
  static async getTaskAttachments(taskId, forceRefresh = false) {
    const cacheKey = `task_attachments_${taskId}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      try {
        console.log(`📎 Fetching attachments for task ${taskId} from: ${API_BASE_URL}/fetchTaskAttachments`);
        const response = await axiosInstance.get(
          `${API_BASE_URL}/fetchTaskAttachments?aa_app_rl_resource_task_uid=${taskId}`
        );

        console.log('📎 Attachments API response:', response.data);

        if (!response.data) {
          console.log('📎 No data in attachments response');
          return [];
        }

        // Handle both array and object responses
        let attachments = [];
        if (Array.isArray(response.data)) {
          attachments = response.data;
        } else if (response.data.attachments) {
          attachments = response.data.attachments;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          attachments = response.data.data;
        } else if (response.data.length > 0 && Array.isArray(response.data)) {
          attachments = response.data;
        }
        
        // If response.data is an array at root level (like your example)
        if (Array.isArray(response.data)) {
          attachments = response.data;
        }
        
        console.log('📎 Parsed attachments:', attachments);
        return attachments;
      } catch (error) {
        // If endpoint doesn't exist or returns 404, return empty array
        if (error.response) {
          console.warn(`📎 Attachments endpoint returned ${error.response.status}:`, error.response.data || error.response.statusText);
          if (error.response.status === 404) {
            console.log(`📎 No attachments endpoint found for task ${taskId}`);
            return [];
          }
        } else {
          console.warn(`📎 Error fetching attachments:`, error.message);
        }
        throw error;
      }
    }, { bypassCache: forceRefresh });
  }

  /**
   * Fetch audit history for a task with caching
   * @param {number} taskId Task ID
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   * @returns {Promise} Promise that resolves to an array of audit records
   */
  static async getTaskAuditHistory(taskId, forceRefresh = false) {
    const cacheKey = `audit_${taskId}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchAuditDetailsById?aa_app_rl_resource_task_uid=${taskId}`
      );

      if (!response.data) {
        throw new Error(`Audit history for task ID ${taskId} not found`);
      }

      // Sort the audit logs by audit ID (assuming higher IDs are newer)
      return response.data.sort(
        (a, b) => b.aa_app_task_audit_id - a.aa_app_task_audit_id
      );
    }, { bypassCache: forceRefresh });
  }

  /**
   * Fetch employee UID by email with caching
   * @param {string} email - User's email address
   * @returns {Promise} Promise that resolves to the employee UID
   */
  static async fetchUserIdByEmail(email) {
    const cacheKey = `user_id_${email}`;
    
    return this.cachedApiCall(cacheKey, async () => {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/fetchUserIdByEmail?user_email=${email}`
      );
      return response.data[0]?.aa_app_employee_uid || null;
    });
  }

  /**
   * Build query string parameters for ticket aging report APIs.
   * @param {Object} filters
   * @param {string|number} [filters.applicationId] - Single application ID
   * @param {Array<string|number>} [filters.applicationIds] - Multiple application IDs (overrides applicationId)
   * @param {Array<string|number>} [filters.statusIds] - Comma-separated status IDs
   * @param {Array<string>} [filters.statuses] - Comma-separated status names
   * @param {string} [filters.status] - Single status name
   * @param {string} [filters.agingBucket] - Single aging bucket code (e.g. LT_30, GT_180)
   * @param {Array<string>} [filters.agingBuckets] - Multiple aging bucket codes
   * @returns {string[]} Query parameter strings
   * @private
   */
  static buildTicketAgingReportQueryParams(filters = {}) {
    const queryParams = [];

    if (filters.applicationIds && Array.isArray(filters.applicationIds) && filters.applicationIds.length > 0) {
      const appParam = filters.applicationIds
        .map((id) => encodeURIComponent(String(id).trim()))
        .filter((s) => s !== "")
        .join(",");
      if (appParam) {
        queryParams.push(`application_ids=${appParam}`);
      }
    } else if (
      filters.applicationId !== undefined &&
      filters.applicationId !== null &&
      String(filters.applicationId).trim() !== ""
    ) {
      queryParams.push(`application_id=${encodeURIComponent(String(filters.applicationId).trim())}`);
    }

    if (filters.statusIds && Array.isArray(filters.statusIds) && filters.statusIds.length > 0) {
      const statusIdParam = filters.statusIds
        .map((id) => encodeURIComponent(String(id).trim()))
        .filter((s) => s !== "")
        .join(",");
      if (statusIdParam) {
        queryParams.push(`status_ids=${statusIdParam}`);
      }
    } else if (filters.statuses && Array.isArray(filters.statuses) && filters.statuses.length > 0) {
      const statusParam = filters.statuses
        .map((s) => encodeURIComponent(String(s).trim()))
        .filter((s) => s !== "")
        .join(",");
      if (statusParam) {
        queryParams.push(`statuses=${statusParam}`);
      }
    } else if (filters.status && String(filters.status).trim() !== "") {
      queryParams.push(`status=${encodeURIComponent(String(filters.status).trim())}`);
    }

    if (filters.agingBuckets && Array.isArray(filters.agingBuckets) && filters.agingBuckets.length > 0) {
      const bucketParam = filters.agingBuckets
        .map((b) => encodeURIComponent(String(b).trim()))
        .filter((s) => s !== "")
        .join(",");
      if (bucketParam) {
        queryParams.push(`aging_buckets=${bucketParam}`);
      }
    } else if (filters.agingBucket && String(filters.agingBucket).trim() !== "") {
      queryParams.push(`aging_bucket=${encodeURIComponent(String(filters.agingBucket).trim())}`);
    }

    if (filters.taskTypes && Array.isArray(filters.taskTypes) && filters.taskTypes.length > 0) {
      const taskTypeParam = filters.taskTypes
        .map((t) => encodeURIComponent(String(t).trim()))
        .filter((s) => s !== "")
        .join(",");
      if (taskTypeParam) {
        queryParams.push(`task_types=${taskTypeParam}`);
      }
    }

    return queryParams;
  }

  /**
   * Fetch ticket aging report summary (application-wise bucket counts).
   * @param {Object} filters - See buildTicketAgingReportQueryParams
   * @param {boolean} [forceRefresh=false]
   * @returns {Promise<Array>}
   */
  static async fetchTicketAgingReport(filters = {}, forceRefresh = false) {
    const cacheKey = `ticket_aging_report_${JSON.stringify(filters)}`;

    return this.cachedApiCall(
      cacheKey,
      async () => {
        let url = `${API_BASE_URL}/fetchTicketAgingReport`;
        const queryParams = this.buildTicketAgingReportQueryParams(filters);
        if (queryParams.length > 0) {
          url += `?${queryParams.join("&")}`;
        }
        console.log("📊 fetchTicketAgingReport URL:", url);
        const response = await axiosInstance.get(url);
        if (Array.isArray(response.data)) return response.data;
        if (response.data && Array.isArray(response.data.data)) return response.data.data;
        return [];
      },
      { bypassCache: forceRefresh }
    );
  }

  /**
   * Fetch ticket aging report details (ticket-level rows).
   * @param {Object} filters - See buildTicketAgingReportQueryParams
   * @param {boolean} [forceRefresh=false]
   * @returns {Promise<Array>}
   */
  static async fetchTicketAgingReportDetails(filters = {}, forceRefresh = false) {
    const cacheKey = `ticket_aging_report_details_${JSON.stringify(filters)}`;

    return this.cachedApiCall(
      cacheKey,
      async () => {
        let url = `${API_BASE_URL}/fetchTicketAgingReportDetails`;
        const queryParams = this.buildTicketAgingReportQueryParams(filters);
        if (queryParams.length > 0) {
          url += `?${queryParams.join("&")}`;
        }
        console.log("📊 fetchTicketAgingReportDetails URL:", url);
        const response = await axiosInstance.get(url);
        if (Array.isArray(response.data)) return response.data;
        if (response.data && Array.isArray(response.data.data)) return response.data.data;
        return [];
      },
      { bypassCache: forceRefresh }
    );
  }

  /**
   * Cache management methods
   */
  
  /**
   * Invalidate all task-related caches
   * @private
   */
  static invalidateTasksCaches() {
    const keysToRemove = [];
    
    for (const key of cache.data.keys()) {
      if (key.startsWith('all_tasks_') || 
          key.startsWith('dashboard_stats_') || 
          key.startsWith('task_') ||
          key.startsWith('audit_')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      cache.data.delete(key);
      cache.timestamps.delete(key);
    });
    
    console.log(`🗑️ Invalidated ${keysToRemove.length} task-related cache entries`);
  }

  /**
   * Clear all caches
   */
  static clearAllCaches() {
    cache.clear();
    console.log("🗑️ All caches cleared");
  }

  /**
   * Clear expired caches
   */
  static clearExpiredCaches() {
    cache.clearExpired();
    console.log("🗑️ Expired caches cleared");
  }

  /**
   * Get cache statistics
   */
  static getCacheStats() {
    return {
      totalEntries: cache.data.size,
      entries: Array.from(cache.data.keys()),
      timestamps: Array.from(cache.timestamps.entries())
    };
  }

  /**
   * Static mapping methods (no need to cache these)
   */
  
  /**
   * Get task status mapping
   * @returns {Object} Status mapping
   */
  static getStatusMapping() {
    return {
      1: "Not started",
      2: "In Progress",
      3: "Completed",
      4: "Cancelled",
    };
  }

  /**
   * Get status name from status ID
   * @param {number} statusId Status ID
   * @returns {string} Status name
   */
  static getStatusNameById(statusId) {
    const statusMap = this.getStatusMapping();
    return statusMap[statusId] || "Unknown";
  }

  /**
   * Get status ID from status name
   * @param {string} statusName Status name
   * @returns {number|null} Status ID or null if not found
   */
  static getStatusIdByName(statusName) {
    const statusMap = this.getStatusMapping();
    const entries = Object.entries(statusMap);
    const entry = entries.find(([_, name]) => name === statusName);
    return entry ? parseInt(entry[0]) : null;
  }
}

// Auto-cleanup expired caches every 10 minutes
setInterval(() => {
  TaskService.clearExpiredCaches();
}, 10 * 60 * 1000);

export default TaskService;